/**
 * Buy Program Pricing Utilities
 *
 * Provides calculation functions for Buy Program reimbursement pricing.
 * These functions handle the ceiling price / actual price model where:
 * - catalog.unitPrice = ceiling price (maximum allowed)
 * - actualPrice = user-entered purchase price (must be <= ceiling)
 * - tax/shipping = per-unit amounts
 * - lineTotal = (actualPrice + tax + shipping) × quantity
 */

import type { IssueBuyProgramCartItem } from './types';

/**
 * Validation result for pricing inputs
 */
export type PricingValidationResult = { valid: true } | { valid: false; error: string };

/**
 * Calculate the line total for a single cart item.
 * Formula: (actualPrice + tax + shipping) × quantity
 *
 * @param item - The cart item with pricing fields
 * @returns The line total amount
 */
export function calculateLineTotal(item: IssueBuyProgramCartItem): number {
  const { actualPrice, tax, shipping, quantity } = item;
  return (actualPrice + tax + shipping) * quantity;
}

/**
 * Calculate the total reimbursement amount for all Buy Program cart items.
 *
 * @param items - Array of cart items
 * @returns The total reimbursement amount
 */
export function calculateBuyProgramTotal(items: IssueBuyProgramCartItem[]): number {
  return items.reduce((sum, item) => sum + calculateLineTotal(item), 0);
}

/**
 * Validate pricing inputs before adding to cart.
 *
 * Rules:
 * - actualPrice must be provided and > 0
 * - actualPrice must be <= ceiling price (catalog.unitPrice)
 * - tax must be >= 0
 * - shipping must be >= 0
 *
 * @param actualPrice - User-entered actual price
 * @param tax - User-entered tax per unit
 * @param shipping - User-entered shipping per unit
 * @param ceilingPrice - Maximum allowed price from catalog
 * @param options - Optional validation options
 * @param options.requireCeiling - When true, blocks the add if ceilingPrice is null/undefined
 *   (use for restricted (buy) program items where a max price must always be configured)
 * @returns Validation result with error message if invalid
 */
export function validatePricingInputs(
  actualPrice: number,
  tax: number,
  shipping: number,
  ceilingPrice: number | null | undefined,
  options?: { requireCeiling?: boolean },
): PricingValidationResult {
  // Validate actual price is provided
  if (!actualPrice || actualPrice <= 0) {
    return { valid: false, error: 'Please enter the actual price before adding to cart' };
  }

  // For restricted (buy) program items, a max price must be configured in the catalog
  if (options?.requireCeiling && ceilingPrice == null) {
    return {
      valid: false,
      error: 'Max price is not configured for this item. Contact your administrator.',
    };
  }

  // Validate actual price is not greater than ceiling price
  const ceiling = ceilingPrice ?? Infinity;
  if (actualPrice > ceiling) {
    return {
      valid: false,
      error: `Actual price cannot exceed the max price of $${ceiling.toFixed(2)}`,
    };
  }

  // Validate tax and shipping are not negative
  if (tax < 0 || shipping < 0) {
    return { valid: false, error: 'Tax and shipping amounts cannot be negative' };
  }

  return { valid: true };
}

/**
 * Parse a pricing input string to a number.
 * Returns 0 for empty/invalid inputs.
 *
 * @param value - String value from input field
 * @returns Parsed number or 0
 */
export function parsePricingInput(value: string | undefined | null): number {
  if (!value) return 0;
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Format a number as USD currency string.
 *
 * @param amount - The amount to format
 * @returns Formatted currency string (e.g., "$123.45")
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

/**
 * Summary of pricing details for a single cart item, formatted for display.
 */
export type PricingSummary = {
  actualPrice: string;
  tax: string | null;
  shipping: string | null;
  quantity: number;
  lineTotal: string;
};

/**
 * Build a human-readable pricing summary for a cart item.
 *
 * @param item - The cart item with pricing fields
 * @returns Formatted pricing summary
 */
export function buildPricingSummary(item: IssueBuyProgramCartItem): PricingSummary {
  return {
    actualPrice: `${formatCurrency(item.actualPrice)}/ea`,
    tax: item.tax > 0 ? `Tax: ${formatCurrency(item.tax)}/ea` : null,
    shipping: item.shipping > 0 ? `Ship: ${formatCurrency(item.shipping)}/ea` : null,
    quantity: item.quantity,
    lineTotal: formatCurrency(calculateLineTotal(item)),
  };
}

/**
 * Determine whether a Buy Program cart item is missing required pricing/receipt
 * data (e.g. bulk-added via "Add All Available" before the user fills it in).
 *
 * @param item - The cart item to check
 * @returns True when actualPrice is not yet set or receiptDate is missing
 */
export function isBuyProgramCartItemIncomplete(item: IssueBuyProgramCartItem): boolean {
  return item.actualPrice <= 0 || !item.receiptDate;
}
